import React, { useState, useEffect, useMemo, useRef, useContext } from 'react';
import { css } from '@emotion/react';
import { DialogContent } from '@material-ui/core';
import { useClientUser } from '@/hooks';
import { ResponsiveSidebarDialog } from '@/styles/blocks';
import { useServiceCatalog } from '../../hooks/serviceCatalog';
import { usePcMilerConfig } from '../../hooks/pcMiler';
import { useEditModalCallbacks, useEditModalDynamicFields } from '../../hooks/modal';
import { customerHasServiceDefinitions, isServiceCallbackOrder } from '../../utilities/serviceCallback';
import { useQuery } from '@apollo/client';
import { captureException } from '@sentry/react';
import { resolveEditOrderTabs } from './tabs/resolveTabs';
import { QUERY_ITEM_SKU } from '../../graphql/queries';
import { UserContext } from '@/components/App';
import { ModalContext, DEFAULT_CALLBACKS, DEFAULT_OPTIONS } from './context';
import EditModalContent from './components/EditModalContent';
import ModalBanners, { ResyncNotice } from './components/ModalBanners';
import ResyncOverlay, { CheckingProgress } from './components/ResyncOverlay';
import ModalHeader from './components/ModalHeader';
import ModalFooter from './components/ModalFooter';
import VersionConflictDialog from './components/VersionConflictDialog';
import { useOrderDraft } from './hooks/useOrderDraft';
import { useOrderVersionGuard } from './hooks/useOrderVersionGuard';
import { useUserStarted } from './hooks/useUserStarted';
import { useOrderValidation } from './hooks/useOrderValidation';
import { useSaveOrder } from './hooks/useSaveOrder';
import { useSavedCustomer } from './hooks/useSavedCustomer';
import { useModalContextValue } from './hooks/useModalContextValue';

// Other files import the context from the folder's index.
export { ModalContext };

const initNote = {
    private: false,
    notify: false,
    note: '',
    type: null,
};

/**
 * Full-screen editor for one order. It edits a copy and hands the result to `callbacks.onSave`.
 * The caller does the database write.
 *
 * @param {object} props
 * @param {object} [props.order] The order to edit. The modal renders nothing until this is set.
 * @param {boolean} props.open Whether the modal is showing. Set it separately from `order` if you like.
 * @param {string} [props.initTab] A value from `EDIT_ORDER_TABS` to scroll to on open.
 * @param {number} [props.initTabOffset] Pixels to leave above `initTab` when scrolling to it.
 * @param {string[]} [props.tabs] Which `EDIT_ORDER_TABS` to show. Leave it out for the default set.
 * @param {string[]|((order: object) => string[])} [props.requiredAttributes] Order fields that must be filled
 * before Done works, or a function that returns them for the current draft.
 * @param {boolean} [props.isNewOrder=false] True for an order that is not saved yet (import review, job
 * review). It turns off the stale-save guard and the live feed, and shows "Save Customer".
 * @param {object} [props.opt] Display options, merged over the defaults: `title`, `readOnly`, `startDirty`,
 * `disableGeocoding`, `disableAddressEditing`, `enablePickupDateEditing`, `enableAdminTab`,
 * `enableAdminTestAccounts`, `customerAvailabilityToggle`.
 * @param {object} props.callbacks
 * @param {(payload: object) => (void|Promise<void>)} props.callbacks.onSave Gets `{ errors, order, items,
 * itemsRemoved, tags, tagsRemoved, documents, orderIds, subscriptions, services, references, ... }`.
 * @param {() => void} props.callbacks.onClose Called on Cancel and when the user closes the form.
 * @param {(error: Error) => void} [props.callbacks.onError] Called when a save step fails or is blocked.
 */
const EditOrderModal = ({
    order,
    open,
    initTab,
    initTabOffset,
    callbacks,
    requiredAttributes,
    tabs,
    opt,
    isNewOrder = false,
}) => {
    const guardRef = useRef(null);
    const draft = useOrderDraft(order);
    const { clone, isDirty, modifyClone, setDirty } = draft;
    const [geoCache, setGeocache] = useState({});
    const [errors, setErrors] = useState({});
    const [createSingleOrderManifest, setCreateSingleOrderManifest] = useState(null);
    const [sendCarrierAssignedEmail, setSendCarrierAssignedEmail] = useState(false);
    const [saveCustomer, setSaveCustomer] = useState(false);
    const [noteInfo, setNoteInfo] = useState(initNote);
    const { client_id, roles, userType, user_id, isOnwardAdmin, isCorporateAdmin, isCarrierAdmin } = useClientUser();
    const { user } = useContext(UserContext);

    const serviceCatalogFallback = useMemo(
        () => ({
            shipperId: clone?.shipper_id || user_id,
        }),
        [clone?.shipper_id, user_id]
    );
    const {
        serviceCatalog,
        serviceCatalogError,
        loading: serviceCatalogLoading,
        refreshCatalogs: refreshServiceCatalogs,
    } = useServiceCatalog(clone, serviceCatalogFallback);

    // Onward and corporate admins can edit read-only (invoiced) orders. An admin impersonating a
    // client is neither, so they see the client's lock.
    const options = useMemo(() => {
        const merged = { ...DEFAULT_OPTIONS, ...(opt || {}) };
        return { ...merged, readOnly: merged.readOnly && !isOnwardAdmin && !isCorporateAdmin };
    }, [opt, isOnwardAdmin, isCorporateAdmin]);

    // When the shipper has PC Miler enabled, distance() on save routes through the backend
    // (no browser SDK for PC Miler); otherwise it uses the browser Google fast path.
    const {
        applies: pcMilerApplies,
        loading: pcMilerLoading,
        error: pcMilerError,
        refetch: refetchPcMiler,
    } = usePcMilerConfig(clone?.shipper_id);

    const pcMilerUnresolved = pcMilerLoading || !!pcMilerError;

    const { save, saveInflight } = useSaveOrder({
        order,
        draft,
        guardRef,
        callbacks,
        pcMilerApplies,
        errors,
        isNewOrder,
        identity: { roles, user, user_id, client_id, userType },
        note: { noteInfo, resetNote: () => setNoteInfo(initNote) },
        saveCustomer: { checked: saveCustomer, clear: () => setSaveCustomer(false) },
        singleOrderManifest: { manifest: createSingleOrderManifest, clear: () => setCreateSingleOrderManifest(null) },
        carrierAssignedEmail: { pending: sendCarrierAssignedEmail, clear: () => setSendCarrierAssignedEmail(false) },
    });
    const { userStartedRef, captureProps } = useUserStarted({ open, order });
    const guard = useOrderVersionGuard({
        order,
        open,
        isNewOrder,
        readOnly: options.readOnly,
        draft,
        userStartedRef,
        saveInflight,
        callbacks,
    });
    guardRef.current = guard;
    const {
        resync,
        resyncLoaded,
        dismissResyncNotice,
        liveChange,
        versionConflict,
        reloading,
        reloadError,
        runOwnWrite,
        refetchOrder,
    } = guard;
    // The body can't take focus or clicks while the latest order loads into it.
    const bodyRef = useRef(null);
    useEffect(() => {
        bodyRef.current?.toggleAttribute('inert', resync === 'loading');
    }, [resync]);

    const showAdminTab = isOnwardAdmin || options.enableAdminTab;
    const showAdminRatesTab =
        (isCorporateAdmin || isCarrierAdmin) &&
        !showAdminTab &&
        clone?.shipper_rate_override?.marketplace_tier !== 'T3_FULLY_MANAGED';

    const isServiceCallback = useMemo(() => isServiceCallbackOrder(clone), [clone]);
    // Show the SERVICES tab when the customer has service definitions configured (customer
    // config, not whether this specific order carries service lines).
    const showServicesTab = customerHasServiceDefinitions(clone, serviceCatalog);

    const combinedTabs = useMemo(
        () =>
            resolveEditOrderTabs({
                tabs,
                order: clone,
                showAdminTab,
                showAdminRatesTab,
                isServiceCallback,
                showServicesTab,
            }),
        [
            tabs,
            clone.has_middle_mile_origin,
            clone.is_middle_mile,
            clone.carrier_id,
            clone.order_type,
            showAdminTab,
            showAdminRatesTab,
            isServiceCallback,
            showServicesTab,
        ]
    );

    // Reset noteInfo when modal closes
    useEffect(() => {
        if (!open) {
            setNoteInfo(initNote);
        }
    }, [open]);

    const { data: skus } = useQuery(QUERY_ITEM_SKU, {
        variables: {
            client_id: clone.shipper_id,
        },
        skip: !clone.shipper_id,
        onError: (err) => {
            console.error(err);
            captureException(err);
        },
    });

    // Ordering is QUERY_ITEM_SKU's job.
    const skusSorted = useMemo(() => (skus?.results || []).map((row) => row.sku), [skus]);

    const validation = useOrderValidation({
        draft,
        tabs: combinedTabs,
        requiredAttributes,
        options,
        serviceCatalog,
    });

    const { isValid } = validation;

    // Derived-field recomputation (order_updated_hash, full_address, tz-shifted dates)
    // runs through the raw setter, separate from field-level dirty tracking.
    useEditModalDynamicFields(clone, modifyClone);

    const modalCallbacks = useEditModalCallbacks(
        {
            geoCache,
            clone,
            errors,
        },
        {
            modifyClone,
            setGeocache,
            setErrors,
            onError: callbacks.onError || DEFAULT_CALLBACKS.onError,
            setDirty,
        }
    );

    const { savedCustomer, onSavedCustomerSelect, deleteSavedCustomer } = useSavedCustomer({ clone, modalCallbacks });

    const modalContextValue = useModalContextValue({
        skus: skusSorted,
        clone,
        serviceCatalog,
        serviceCatalogError,
        serviceCatalogLoading,
        serviceCatalogShipperId: serviceCatalogFallback.shipperId,
        tabs: combinedTabs,
        options,
        initTab,
        initTabOffset,
        validation,
        errors,
        isDirty,
        createSingleOrderManifest,
        setCreateSingleOrderManifest,
        sendCarrierAssignedEmail,
        setSendCarrierAssignedEmail,
        savedCustomer,
        setSaveCustomer,
        noteInfo,
        setNoteInfo,
        callbacks,
        modalCallbacks,
        onSavedCustomerSelect,
        deleteSavedCustomer,
        refetchOrder,
        runOwnWrite,
        refreshServiceCatalogs,
    });

    // Keep the context hook above this early return.
    if (!order) {
        return null;
    }

    return (
        <ResponsiveSidebarDialog
            open={open}
            onClose={callbacks.onClose}
            fullWidth={true}
            maxWidth="lg"
            {...captureProps}
            css={css`
                .MuiDialog-container {
                    align-items: normal;
                }
            `}
        >
            <ModalHeader order={order} clone={clone} title={options.title} onClose={callbacks.onClose} />
            {resync === 'checking' && <CheckingProgress />}
            <ResyncNotice show={resyncLoaded} onDismiss={dismissResyncNotice} />
            <ModalContext.Provider value={modalContextValue}>
                <DialogContent
                    css={css`
                        position: relative;
                        width: 100%;
                        padding: 0;
                        display: flex;
                        flex-direction: row;
                    `}
                >
                    <div
                        ref={bodyRef}
                        aria-busy={resync === 'loading'}
                        css={css`
                            display: flex;
                            flex-direction: row;
                            width: 100%;
                        `}
                    >
                        <EditModalContent />
                    </div>
                    {resync === 'loading' && <ResyncOverlay />}
                </DialogContent>
            </ModalContext.Provider>
            <ModalBanners
                order={order}
                showAdminTab={showAdminTab}
                pcMilerError={pcMilerError}
                onRetryPcMiler={refetchPcMiler}
            />
            <ModalFooter
                saveInflight={saveInflight}
                isNewOrder={isNewOrder}
                saveCustomer={saveCustomer}
                onSaveCustomerChange={setSaveCustomer}
                canSave={isValid && !pcMilerUnresolved}
                // A live change blocks saving until the order is reloaded.
                saveBlocked={options.readOnly || !!liveChange}
                checking={!!resync}
                onSave={save}
                onCancel={callbacks.onClose}
            />
            {/* The live variant wins when both apply. */}
            <VersionConflictDialog
                open={!!versionConflict}
                variant={versionConflict?.variant}
                row={versionConflict?.row}
                yourStatus={clone.order_status}
                hasNote={!!noteInfo.note}
                reloading={reloading}
                error={reloadError}
                onReload={guard.reload}
                onKeepEditing={guard.keepEditing}
                onCloseForm={callbacks.onClose}
            />
        </ResponsiveSidebarDialog>
    );
};

export default EditOrderModal;
